import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import QuotePdfPreviewPanel from '../components/QuotePdfPreviewPanel';
import MultiProductChoice from '../components/MultiProductChoice';
import { auth, db } from '../firebase';
import { buildPdfPayloadFromText } from '../helpers/buildPdfPayloadFromText';

const SAMPLE_MULTI_TEXT = `LabelsLab multi-product quote

--- BAGS ---
Coffee pouch bags
Stand up pouch
style #4
size 101.1 W x 127.4 H x 50.8 G
pet/vmpet/pe
gloss lamination
with tear notch, regular zipper and valve
5 SKUs
Total quantities: 5,000, 10,000 and 20,000
Cost per piece: 0.44 | 0.3252 | 0.2245
Shipping:
Express air $235
regular air $178
express sea $94
regular sea $56

--- LABELS ---
Labels quote
White Bopp labels
Material: White Bopp
Thickness: 120 mil
Finish: Lamination
Special Effects: N/A
Rolling Direction: Hand applied
Shipping method: express air

Etiqueta pequena - 2 cm x 3.5 cm
SKU 16-20
Quantities: 4,000 | 5,000 | 10,000
Cost per piece: 0.1529 | 0.1263 | 0.0680

Etiqueta Grande - 6 cm x 5 cm
SKU 1
Quantities: 1,000 | 2,000 | 3,000
Cost per piece: 0.5271 | 0.2873 | 0.2270

Order lead time: 5-10 business days`;

const SAMPLE_BAG_TEXT = `Coffee pouch bags
Stand up pouch
style #4
size 101.1 W x 127.4 H x 50.8 G
pet/vmpet/pe
gloss lamination
with tear notch, regular zipper and valve
5 SKUs
Total quantities: 5,000, 10,000 and 20,000
Cost per piece: 0.44 | 0.3252 | 0.2245
Shipping:
Express air $235
regular air $178
express sea $94
regular sea $56`;

const SAMPLE_BOX_TEXT = `Retail product boxes
Reverse tuck end box
style #2
size 152.4 W x 228.6 H x 63.5 D
350gsm white cardstock
matte lamination
with window cutout and spot UV
3 SKUs
Total quantities: 2,500, 5,000 and 10,000
Cost per piece: 0.82 | 0.6540 | 0.4895
Shipping:
Express air $185
regular air $142
express sea $78
regular sea $48`;

const SAMPLE_LABEL_TEXT = `Labels quote
White Bopp labels
Material: White Bopp
Thickness: 120 mil
Finish: Lamination
Special Effects: N/A
Rolling Direction: Hand applied
Shipping method: express air

Etiqueta pequeña - 2 cm x 3.5 cm
SKU 16-20
Quantities: 4,000 | 5,000 | 10,000
Cost per piece: 0.1529 | 0.1263 | 0.0680

Etiqueta Grande - 6 cm x 5 cm
SKU 1
Quantities: 1,000 | 2,000 | 3,000
Cost per piece: 0.5271 | 0.2873 | 0.2270

Order lead time: 5-10 business days`;

const SAMPLE_TWO_BAGS_TEXT = `LabelsLab quote — two bag sizes

--- BAG 1 — Small coffee pouch ---
Coffee pouch bags
Stand up pouch
style #4
size 101.1 W x 127.4 H x 50.8 G
pet/vmpet/pe
gloss lamination
with tear notch, regular zipper and valve
3 SKUs
Total quantities: 5,000, 10,000 and 20,000
Cost per piece: 0.44 | 0.3252 | 0.2245
Shipping:
Express air $235
regular air $178
express sea $94
regular sea $56

--- BAG 2 — Large snack pouch ---
Snack pouch bags
Stand up pouch
style #4
size 152.4 W x 203.2 H x 63.5 G
matte opp / vmpet / pe
matte lamination
with tear notch and regular zipper
2 SKUs
Total quantities: 3,000, 6,000 and 12,000
Cost per piece: 0.52 | 0.3890 | 0.2810
Shipping:
Express air $265
regular air $195
express sea $108
regular sea $62`;

const GenerateQuotePdf = () => {
  const [user] = useAuthState(auth);
  const [step, setStep] = useState('input');
  const [companyName, setCompanyName] = useState('');
  const [attentionTo, setAttentionTo] = useState('');
  const [salesRepName, setSalesRepName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [specText, setSpecText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [pdfPayload, setPdfPayload] = useState(null);

  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setSalesRepName(userDoc.data().name || user.displayName || '');
        } else {
          setSalesRepName(user.displayName || '');
        }
      } catch (error) {
        console.error(error);
      }
    };
    loadProfile();
  }, [user]);

  const handleGenerate = async () => {
    if (!specText.trim()) {
      toast.error('Paste your quote specs first.');
      return;
    }
    if (!companyName.trim()) {
      toast.error('Client name is required.');
      return;
    }

    setGenerating(true);
    try {
      const payload = await buildPdfPayloadFromText(
        specText,
        {
          companyName,
          attentionTo,
          salesRepName,
          projectId,
          projectDescription,
        },
        {
          userId: user?.uid || null,
          metadata: {
            companyName,
            projectId,
            salesRepName,
            attentionTo,
          },
        },
      );
      if (payload.products?.length > 1) {
        setPdfPayload(payload);
        setStep('choose');
        toast.success(`Found ${payload.products.length} products — choose PDF layout.`);
      } else {
        setPdfPayload(payload);
        setStep('preview');
        toast.success('Preview ready — review and edit before downloading.');
      }
    } catch (error) {
      toast.error(error.message || 'Could not generate PDF.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Header />
      <div className="parent-container pt-5 h-100 pb-10 p-4 sm:ml-64">
        <div className={`mx-auto bg-white rounded-lg shadow-md p-6 md:p-8 ${step === 'preview' ? 'max-w-7xl' : 'max-w-4xl'}`}>
          {step === 'input' ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Generate Quote PDF</h1>
              <p className="text-gray-600 mb-6">
                Paste customer specs below — bag/box style, sizes, material, quantities, pricing, and
                shipping. No saved quote needed. You will get a preview to edit before downloading.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Client *</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Attention to</label>
                  <input
                    type="text"
                    value={attentionTo}
                    onChange={(e) => setAttentionTo(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Project ID</label>
                  <input
                    type="text"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. LL1001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Prepared by</label>
                  <input
                    type="text"
                    value={salesRepName}
                    onChange={(e) => setSalesRepName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Product description</label>
                  <input
                    type="text"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. Order #11066 reprint"
                  />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-800">
                    Quote specs (paste everything here)
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSpecText(SAMPLE_BAG_TEXT)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Load bag example
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSpecText(SAMPLE_BOX_TEXT)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Load box example
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSpecText(SAMPLE_LABEL_TEXT)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Load label example
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSpecText(SAMPLE_MULTI_TEXT)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Load multi-product
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSpecText(SAMPLE_TWO_BAGS_TEXT)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Load 2 bags (same type)
                    </button>
                  </div>
                </div>
                <textarea
                  value={specText}
                  onChange={(e) => setSpecText(e.target.value)}
                  rows={14}
                  placeholder="Paste bag/box specs, style #, size, material, finish, SKUs, quantities, cost per piece, shipping costs..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-mono leading-relaxed"
                />
              </div>

              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-60"
              >
                {generating ? 'Parsing specs…' : 'Generate preview'}
              </button>
            </>
          ) : step === 'choose' && pdfPayload ? (
            <MultiProductChoice
              payload={pdfPayload}
              onConfirm={(next) => {
                setPdfPayload(next);
                setStep('preview');
              }}
              onBack={() => setStep('input')}
            />
          ) : (
            pdfPayload && (
              <QuotePdfPreviewPanel
                payload={pdfPayload}
                onPayloadChange={setPdfPayload}
                onBack={() => setStep('input')}
              />
            )
          )}
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={4000} />
    </>
  );
};

export default GenerateQuotePdf;
